import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import { useCallback, useEffect, useRef, useState } from "react";
import facebookLogo from "../../../../images/icons/facebook-logo-color.svg";
import googleMapsLogo from "../../../../images/icons/googlemaps-logo-color.svg";
import trustpilotLogo from "../../../../images/icons/trustpilot-logo-color.svg";
import i18n from "../../../../utils/i18n.js";
import classes from "./testimonialCarousel.module.scss";

const TESTIMONIALS = [
  {
    id: "dave",
    name: "Dave N",
    text: i18n.t("searchflow.testimonial1"),
    source: "trustpilot",
    sourceLabel: "Trustpilot",
    sourceLogo: trustpilotLogo,
  },
  {
    id: "noemi",
    name: "Bertalan Noémi",
    text: i18n.t("searchflow.testimonial2"),
    source: "googlemaps",
    sourceLabel: "Google Maps",
    sourceLogo: googleMapsLogo,
  },
  {
    id: "lili",
    name: "Lili Tököli",
    text: i18n.t("searchflow.testimonial3"),
    source: "facebook",
    sourceLabel: "Facebook",
    sourceLogo: facebookLogo,
  },
];

const LAST_INDEX = TESTIMONIALS.length - 1;
const GAP_PX = 16;
const DRAG_THRESHOLD_PX = 48;

const StarRating = () => (
  <span className={classes.stars} aria-label={i18n.t("searchflow.starratinglabel")}>
    {Array.from({ length: 5 }, (_, index) => (
      <StarRoundedIcon key={index} className={classes.starIcon} aria-hidden="true" />
    ))}
  </span>
);

const TestimonialCarousel = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [stepWidth, setStepWidth] = useState(0);
  const [maxTranslate, setMaxTranslate] = useState(0);
  const [cardWidth, setCardWidth] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(0);

  const viewportRef = useRef(null);
  const railRef = useRef(null);
  const cardRef = useRef(null);
  const dragStateRef = useRef({
    active: false,
    startX: 0,
    pointerId: null,
  });
  const activeIndexRef = useRef(activeIndex);
  activeIndexRef.current = activeIndex;

  const getBaseTranslate = useCallback((index, step, max, viewport, card) => {
    if (index <= 0 || step <= 0) {
      return 0;
    }
    if (index >= LAST_INDEX) {
      return max;
    }
    // Center middle cards so left/right neighbors peek equally.
    const centered = index * step - (viewport - card) / 2;
    return Math.max(0, Math.min(centered, max));
  }, []);

  const measure = useCallback(() => {
    const viewport = viewportRef.current;
    const rail = railRef.current;
    const card = cardRef.current;
    if (!viewport || !rail || !card) {
      return;
    }

    const nextCardWidth = card.offsetWidth;
    const nextViewportWidth = viewport.clientWidth;
    const nextStep = nextCardWidth + GAP_PX;
    const nextMax = Math.max(0, rail.scrollWidth - nextViewportWidth);
    setCardWidth(nextCardWidth);
    setViewportWidth(nextViewportWidth);
    setStepWidth(nextStep);
    setMaxTranslate(nextMax);
  }, []);

  useEffect(() => {
    measure();
    const viewport = viewportRef.current;
    if (!viewport || typeof ResizeObserver === "undefined") {
      return undefined;
    }
    const observer = new ResizeObserver(() => measure());
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [measure]);

  const goTo = useCallback((index) => {
    const next = Math.max(0, Math.min(index, LAST_INDEX));
    setActiveIndex(next);
    setDragOffset(0);
  }, []);

  const clampDragOffset = useCallback((rawDelta, index) => {
    if (index <= 0 && rawDelta > 0) {
      return 0;
    }
    if (index >= LAST_INDEX && rawDelta < 0) {
      return 0;
    }
    return rawDelta;
  }, []);

  const handlePointerDown = useCallback((event) => {
    if (event.pointerType === "mouse" && event.button !== 0) {
      return;
    }

    dragStateRef.current = {
      active: true,
      startX: event.clientX,
      pointerId: event.pointerId,
    };
    setIsDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  }, []);

  const handlePointerMove = useCallback(
    (event) => {
      const dragState = dragStateRef.current;
      if (!dragState.active || dragState.pointerId !== event.pointerId) {
        return;
      }

      const rawDelta = event.clientX - dragState.startX;
      setDragOffset(clampDragOffset(rawDelta, activeIndexRef.current));
    },
    [clampDragOffset],
  );

  const endDrag = useCallback(
    (event) => {
      const dragState = dragStateRef.current;
      if (!dragState.active || dragState.pointerId !== event.pointerId) {
        return;
      }

      const rawDelta = event.clientX - dragState.startX;
      const delta = clampDragOffset(rawDelta, activeIndexRef.current);
      dragState.active = false;
      dragState.pointerId = null;
      setIsDragging(false);

      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }

      if (delta <= -DRAG_THRESHOLD_PX && activeIndexRef.current < LAST_INDEX) {
        goTo(activeIndexRef.current + 1);
        return;
      }
      if (delta >= DRAG_THRESHOLD_PX && activeIndexRef.current > 0) {
        goTo(activeIndexRef.current - 1);
        return;
      }
      setDragOffset(0);
    },
    [clampDragOffset, goTo],
  );

  const baseTranslate = getBaseTranslate(
    activeIndex,
    stepWidth,
    maxTranslate,
    viewportWidth,
    cardWidth,
  );
  const translateX = -baseTranslate + dragOffset;

  const canGoPrev = activeIndex > 0;
  const canGoNext = activeIndex < LAST_INDEX;

  return (
    <section className={classes.root} aria-label={i18n.t("searchflow.testimonialsectionlabel")}>
      <div
        ref={viewportRef}
        className={`${classes.viewport} ${isDragging ? classes.viewportDragging : ""}`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onDragStart={(event) => event.preventDefault()}
      >
        <div
          ref={railRef}
          className={classes.rail}
          style={{ transform: `translate3d(${translateX}px, 0, 0)` }}
        >
          {TESTIMONIALS.map((item, index) => (
            <article
              key={item.id}
              ref={index === 0 ? cardRef : undefined}
              className={classes.card}
            >
              <div className={classes.cardHeader}>
                <h3 className={classes.name}>{item.name}</h3>
                <StarRating />
              </div>
              <p className={classes.text}>{item.text}</p>
              <div className={classes.cardFooter}>
                <span className={classes.sourceLabel}>{i18n.t("searchflow.reviewsourcelabel")}</span>
                <img
                  src={item.sourceLogo}
                  alt={item.sourceLabel}
                  className={`${classes.sourceLogo} ${
                    classes[`sourceLogo_${item.source}`] || ""
                  }`}
                />
              </div>
            </article>
          ))}
        </div>
      </div>

      <div className={classes.controls}>
        <button
          type="button"
          className={classes.navButton}
          onClick={() => goTo(activeIndex - 1)}
          disabled={!canGoPrev}
          aria-label={i18n.t("searchflow.prevreview")}
        >
          <ChevronLeftIcon aria-hidden="true" />
        </button>

        <div className={classes.dots} role="tablist" aria-label={i18n.t("searchflow.reviewstablist")}>
          {TESTIMONIALS.map((item, index) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={index === activeIndex}
              aria-label={`${index + 1}. ${i18n.t("searchflow.reviewword")}`}
              className={`${classes.dot} ${
                index === activeIndex ? classes.dotActive : ""
              }`}
              onClick={() => goTo(index)}
            />
          ))}
        </div>

        <button
          type="button"
          className={classes.navButton}
          onClick={() => goTo(activeIndex + 1)}
          disabled={!canGoNext}
          aria-label={i18n.t("searchflow.nextreview")}
        >
          <ChevronRightIcon aria-hidden="true" />
        </button>
      </div>
    </section>
  );
};

export default TestimonialCarousel;
